import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  CreditCard as CreditCardIcon,
  TrendingUp,
  Layers,
  Trophy,
  Filter as FilterIcon,
  XCircleIcon,
  Loader2Icon,
  PlusIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Kart türü rozet renkleri — CreditCardsSettings ile aynı paletten.
const TYPE_COLOR = {
  visa: 'bg-blue-600',
  master: 'bg-orange-600',
  amex: 'bg-emerald-600',
  troy: 'bg-red-600',
  other: 'bg-gray-600',
};

const TYPE_LABEL = {
  visa: 'Visa',
  master: 'Mastercard',
  amex: 'Amex',
  troy: 'Troy',
  other: 'Diğer',
};

const MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const fmt = (amount, currency = 'TRY') => {
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount || 0);
  } catch {
    return `${amount} ${currency}`;
  }
};

// Üstte gösterilen KPI kartı — ikon + başlık + değer + isteğe bağlı alt satır.
const KpiCard = ({ icon: Icon, label, value, sub, accent = 'indigo', testId }) => {
  const accents = {
    indigo: 'bg-indigo-50 text-indigo-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    rose: 'bg-rose-50 text-rose-700',
  };
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${accents[accent] || accents.indigo}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
          <div className="text-lg sm:text-xl font-semibold text-gray-900 mt-0.5 truncate">
            {value}
          </div>
          {sub && (
            <div className="text-xs text-gray-500 mt-0.5 truncate">{sub}</div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Kart bazlı özet satırı — üstte kart adı+badge, altta toplam & adet.
const CardSummaryTile = ({ card, active, onClick }) => {
  const color = TYPE_COLOR[card.card_type || 'visa'];
  const label = TYPE_LABEL[card.card_type || 'visa'];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left w-full rounded-xl p-4 transition border-2 ${
        active ? 'border-indigo-500 shadow-sm' : 'border-transparent hover:border-gray-200'
      } bg-gradient-to-br from-slate-800 to-slate-900 text-white`}
      data-testid={`card-tile-${card.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-xs text-white/60 uppercase tracking-wider">Kart</div>
          <div className="text-base font-semibold mt-0.5 truncate">{card.card_name}</div>
          <div className="text-xs text-white/60 mt-0.5 font-mono tracking-wider">
            •••• {card.last4}
          </div>
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded ${color} text-white uppercase`}>
          {label}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-white/50">Toplam</div>
          <div className="text-white font-semibold text-sm mt-0.5">
            {fmt(card.total_amount)}
          </div>
        </div>
        <div>
          <div className="text-white/50">Adet</div>
          <div className="text-white font-semibold text-sm mt-0.5">{card.count}</div>
        </div>
      </div>
    </button>
  );
};

const SoloCardExpenses = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    kpi: { total_amount: 0, total_count: 0, active_cards: 0, total_cards: 0, top_card: null },
    cards: [],
    expenses: [],
    facets: { years: [] },
  });
  const [selectedCardId, setSelectedCardId] = useState('all');
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [monthFilter, setMonthFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (yearFilter !== 'all') params.year = yearFilter;
      if (monthFilter !== 'all') params.month = monthFilter;
      if (selectedCardId !== 'all') params.card_id = selectedCardId;
      const res = await axios.get(`${BACKEND_URL}/api/solo/card-expenses`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Kart harcamaları alınamadı');
    } finally {
      setLoading(false);
    }
  }, [yearFilter, monthFilter, selectedCardId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasCards = data.cards.length > 0;
  const activeCard = useMemo(
    () => data.cards.find((c) => c.id === selectedCardId) || null,
    [data.cards, selectedCardId]
  );

  const clearFilters = () => {
    setYearFilter(String(new Date().getFullYear()));
    setMonthFilter('all');
    setSelectedCardId('all');
  };
  const hasActiveFilter =
    yearFilter !== String(new Date().getFullYear()) ||
    monthFilter !== 'all' ||
    selectedCardId !== 'all';

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="solo-card-expenses-page">
      {/* Başlık */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCardIcon className="w-6 h-6 text-indigo-600" />
            Kart Harcamaları
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Kredi kartlarınızla yaptığınız tüm harcamalar — toplu ve kart bazlı görünüm.
          </p>
        </div>
        <Button
          onClick={() => navigate('/dashboard/expenses/new')}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="card-expenses-new-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Yeni Harcama
        </Button>
      </div>

      {/* KPI Şeridi */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={TrendingUp}
          label="Toplam Kart Harcaması"
          value={fmt(data.kpi.total_amount)}
          sub={`${data.kpi.total_count} işlem`}
          accent="indigo"
          testId="kpi-total"
        />
        <KpiCard
          icon={Layers}
          label="Aktif Kart"
          value={`${data.kpi.active_cards} / ${data.kpi.total_cards}`}
          sub="Harcama yapılan / tanımlı"
          accent="emerald"
          testId="kpi-active"
        />
        <KpiCard
          icon={CreditCardIcon}
          label="Ortalama Kart Başı"
          value={fmt(
            data.kpi.active_cards
              ? data.kpi.total_amount / data.kpi.active_cards
              : 0
          )}
          sub={data.kpi.active_cards ? 'Aktif kart başına' : '—'}
          accent="amber"
          testId="kpi-avg"
        />
        <KpiCard
          icon={Trophy}
          label="En Çok Kullanılan"
          value={data.kpi.top_card
            ? `${data.kpi.top_card.card_name} •${data.kpi.top_card.last4}`
            : '—'}
          sub={data.kpi.top_card ? fmt(data.kpi.top_card.total_amount) : 'Henüz veri yok'}
          accent="rose"
          testId="kpi-top"
        />
      </div>

      {/* Filtreler */}
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm text-gray-700 mb-3">
          <FilterIcon className="w-4 h-4 text-indigo-600" />
          <span className="font-medium">Filtreler</span>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <XCircleIcon className="w-3.5 h-3.5" /> Temizle
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Kart</label>
            <select
              value={selectedCardId}
              onChange={(e) => setSelectedCardId(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              data-testid="filter-card"
            >
              <option value="all">Tüm Kartlar</option>
              {data.cards.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.card_name} •{c.last4}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Yıl</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              data-testid="filter-year"
            >
              <option value="all">Tüm Yıllar</option>
              {(data.facets.years.length
                ? data.facets.years
                : [String(new Date().getFullYear())]
              ).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Ay</label>
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              data-testid="filter-month"
            >
              <option value="all">Tüm Aylar</option>
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{m}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Kart bazlı toplu kırılım */}
      {hasCards ? (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Kart Bazlı Toplam
            </h2>
            <span className="text-xs text-gray-500">Kart seç → aşağıda o karta özel liste</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Tümü seçeneği */}
            <button
              type="button"
              onClick={() => setSelectedCardId('all')}
              className={`text-left rounded-xl p-4 transition border-2 ${
                selectedCardId === 'all' ? 'border-indigo-500 shadow-sm' : 'border-transparent hover:border-gray-200'
              } bg-gradient-to-br from-indigo-500 to-indigo-700 text-white`}
              data-testid="card-tile-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-white/60 uppercase tracking-wider">Toplu</div>
                  <div className="text-base font-semibold mt-0.5">Tüm Kartlar</div>
                </div>
                <Layers className="w-5 h-5 text-white/60" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="text-white/50">Toplam</div>
                  <div className="text-white font-semibold text-sm mt-0.5">
                    {fmt(data.kpi.total_amount)}
                  </div>
                </div>
                <div>
                  <div className="text-white/50">Adet</div>
                  <div className="text-white font-semibold text-sm mt-0.5">{data.kpi.total_count}</div>
                </div>
              </div>
            </button>
            {data.cards.map((c) => (
              <CardSummaryTile
                key={c.id}
                card={c}
                active={c.id === selectedCardId}
                onClick={() => setSelectedCardId(c.id)}
              />
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-center text-gray-500" data-testid="no-cards-warning">
          <CreditCardIcon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <div className="font-medium text-gray-700">Henüz tanımlı kart yok</div>
          <p className="text-sm mt-1">
            Ayarlar &gt; Kredi Kartları menüsünden bankalarınızı ve kartlarınızı ekleyin.
          </p>
          <Button
            className="mt-3 bg-indigo-600 hover:bg-indigo-700"
            onClick={() => navigate('/dashboard/settings?section=credit-cards')}
            data-testid="goto-settings-cards"
          >
            Ayarlara Git
          </Button>
        </Card>
      )}

      {/* Liste — filtreye uyan kart harcamaları */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {selectedCardId === 'all'
              ? 'Tüm Kart Harcamaları'
              : `${activeCard?.card_name || 'Kart'} •${activeCard?.last4 || ''} Harcamaları`}
          </h2>
          <span className="text-xs text-gray-500">{data.expenses.length} kayıt</span>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <Loader2Icon className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
          </div>
        ) : data.expenses.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            Bu filtreye uyan kart harcaması bulunamadı.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="card-expenses-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Tarih</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">İşletme</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Açıklama</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Kategori</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Kart</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Tutar</th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.map((e) => (
                  <tr key={e.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-600">{e.date}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{e.supplier || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{e.description || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{e.category || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                        💳 {e.credit_card?.card_name || 'Kart'} •{e.credit_card?.last4 || '••••'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {fmt(e.amount, e.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SoloCardExpenses;
